import { searchJyotishaTopics } from 'schools/parashari/data/jyotishaTopicSearchIndex';
import { getJyotishaTopicLabel, getJyotishaTopicLabelHi } from 'schools/parashari/data/jyotishaTopicLabels';
import { JYOTISHA_TOPIC_ICONS } from 'schools/parashari/data/jyotishaTopicIcons';
import { isSchoolFilterActive } from 'shared/search/schoolSearchConfig';
import { searchSyllabusTopics } from 'shared/search/syllabusSearchIndex';
import { foldSearchText, scoreSearchMatch } from 'shared/search/searchUtils';
import type {
  GlobalSearchFilter,
  GlobalSearchResult,
  SearchableSchoolSlug,
} from 'shared/search/types';

const MAX_RESULTS = 48;

function queryWords(query: string): string[] {
  return foldSearchText(query)
    .trim()
    .split(/\s+/)
    .filter((w) => w.length >= 2);
}

function mapJyotishaResults(query: string): GlobalSearchResult[] {
  const q = foldSearchText(query).trim();
  const words = queryWords(query);

  return searchJyotishaTopics(query)
    .map((entry) => {
      const titleEn = getJyotishaTopicLabel(entry.id) || entry.tabLabel;
      const titleHi = getJyotishaTopicLabelHi(entry.id) || entry.tabLabel;
      const rank = scoreSearchMatch(
        {
          labelEn: titleEn,
          labelHi: titleHi,
          groupEn: entry.groupLabel,
          terms: entry.terms,
        },
        q,
        words,
      );

      return {
        schoolSlug: 'parashari' as const,
        topicId: entry.id,
        titleEn,
        titleHi,
        groupLabelEn: entry.groupLabel,
        groupLabelHi: entry.groupLabel,
        matchSnippet: entry.matchSnippet ?? '',
        icon: JYOTISHA_TOPIC_ICONS[entry.id] || '✦',
        rank,
        parashariSection: 'jyotisha' as const,
        kind: entry.kind,
      };
    })
    .filter((result) => result.rank > 0);
}

function mergeByRelevance(results: GlobalSearchResult[]): GlobalSearchResult[] {
  return [...results].sort((a, b) => {
    if (b.rank !== a.rank) return b.rank - a.rank;
    const schoolCmp = a.schoolSlug.localeCompare(b.schoolSlug);
    if (schoolCmp !== 0) return schoolCmp;
    return a.titleEn.localeCompare(b.titleEn);
  });
}

export function searchGlobalTopics(
  query: string,
  filter: GlobalSearchFilter = 'all',
): GlobalSearchResult[] {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const buckets: GlobalSearchResult[] = [];

  if (isSchoolFilterActive(filter, 'parashari')) {
    buckets.push(...mapJyotishaResults(trimmed));
  }

  const syllabusSchools: SearchableSchoolSlug[] = [
    'jaimini',
    'kp',
    'bnn',
    'lal-kitab',
    'numerology',
  ];
  for (const slug of syllabusSchools) {
    if (isSchoolFilterActive(filter, slug)) {
      buckets.push(...searchSyllabusTopics(trimmed, slug));
    }
  }

  return mergeByRelevance(buckets).slice(0, MAX_RESULTS);
}
